import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import type { Pagination } from "@/types/api.types";

export interface Club {
  id: number;
  name: string;
  shortName?: string | null;
  logoUrl?: string | null;
  country?: string | null;
  league?: string | null;
  founded?: number | null;
  _count?: { players: number };
}

export interface CreateClubRequest {
  name: string;
  shortName?: string;
  logoUrl?: string;
  country?: string;
  league?: string;
  founded?: number;
}

export interface ClubFilters {
  search?: string;
  league?: string;
  country?: string;
  page?: number;
  limit?: number;
}

export interface ClubFilterOptions {
  leagues: string[];
  countries: string[];
}

export const clubsService = {
  list: async (
    filters?: ClubFilters,
  ): Promise<{ clubs: Club[]; pagination: Pagination }> => {
    const params = new URLSearchParams();
    if (filters?.search) params.append("search", filters.search);
    if (filters?.league) params.append("league", filters.league);
    if (filters?.country) params.append("country", filters.country);
    if (filters?.page) params.append("page", String(filters.page));
    if (filters?.limit) params.append("limit", String(filters.limit));
    const qs = params.toString();
    return apiClient.get(
      qs ? `${API_ENDPOINTS.CLUBS.LIST}?${qs}` : API_ENDPOINTS.CLUBS.LIST,
    );
  },

  getFilters: async (): Promise<ClubFilterOptions> => {
    return apiClient.get(API_ENDPOINTS.CLUBS.FILTERS);
  },

  create: async (data: CreateClubRequest): Promise<Club> => {
    return apiClient.post(API_ENDPOINTS.CLUBS.CREATE, data);
  },

  update: async (
    id: number,
    data: Partial<CreateClubRequest>,
  ): Promise<Club> => {
    return apiClient.patch(API_ENDPOINTS.CLUBS.UPDATE(id), data);
  },

  delete: async (id: number): Promise<{ message: string }> => {
    return apiClient.delete(API_ENDPOINTS.CLUBS.DELETE(id));
  },

  uploadLogo: async (file: File): Promise<{ url: string }> => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post(API_ENDPOINTS.CLUBS.UPLOAD_LOGO, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};
